import classnames from 'classnames';
import {
  CKBox,
  CKContainerProps,
  CKLayoutGrid,
  CKSkeleton,
  includeChildrenByDisplayName,
} from 'clearkit';
import { FC } from 'react';

import {
  MatchRateAttributes,
  MatchRateAttributeType,
} from './MatchRateAttributes';

export type MatchRateData = {
  match_rate: number;
  with_data_count: number;
  attributes: MatchRateAttributeType[];
};

type HomeMatchRateCardProps = CKContainerProps & {
  isLoading?: boolean;
  data: MatchRateData;
  type: 'companies' | 'people';
};

type MatchRateComposition = {
  Footer: FC<CKContainerProps>;
  Heading: FC<CKContainerProps>;
};

const HomeMatchRateMetric = ({
  className,
  children,
  isLoading,
  ...rest
}: CKContainerProps & {
  isLoading?: boolean;
}) => (
  <div
    {...rest}
    className={classnames(
      className,
      'text-[1.5rem] font-semibold leading-tight text-gray-900',
    )}
  >
    <CKSkeleton className="w-[6ch]" isLoading={isLoading} label="loading">
      <span>{children}</span>
    </CKSkeleton>
  </div>
);

export const HomeMatchRateCard: FC<HomeMatchRateCardProps> &
  MatchRateComposition = ({
  children,
  className,
  data,
  isLoading,
  type,
  ...rest
}) => {
  return (
    <CKBox
      {...rest}
      className={classnames('text-gray-900 overflow-hidden', className)}
    >
      <CKLayoutGrid gridTemplateColumns="13.75rem 1fr">
        <div className="p-6  border-r border-gray-200">
          {includeChildrenByDisplayName({
            children,
            componentDisplayName: 'HomeMatchRateCard.Heading',
          })}
          <HomeMatchRateMetric
            className={classnames('mb-6 ', {
              'text-green': data?.match_rate,
            })}
            isLoading={isLoading}
          >
            {data?.match_rate
              ? `${Math.round((data?.match_rate ?? 0) * 100)}%`
              : '--'}
          </HomeMatchRateMetric>
          <HomeMatchRateCard.Heading>
            <span className="capitalize">{type}</span> analyzed
          </HomeMatchRateCard.Heading>
          <HomeMatchRateMetric isLoading={isLoading}>
            {data?.with_data_count?.toLocaleString('en-us') || '--'}
          </HomeMatchRateMetric>
        </div>
        <div className="p-6">
          <HomeMatchRateCard.Heading className="mb-6">
            Top Clearbit attribute match rates for {type}
          </HomeMatchRateCard.Heading>
          <MatchRateAttributes
            attributes={data?.attributes}
            isLoading={isLoading}
          />
        </div>
      </CKLayoutGrid>
      {includeChildrenByDisplayName({
        children,
        componentDisplayName: 'HomeMatchRateCard.Footer',
      })}
    </CKBox>
  );
};

HomeMatchRateCard.Footer = ({ className, ...rest }) => (
  <footer
    {...rest}
    className={classnames(
      className,
      'bg-gray-0 p-6 text-sm border-t border-gray-200',
    )}
  />
);
HomeMatchRateCard.Footer.displayName = 'HomeMatchRateCard.Footer';

HomeMatchRateCard.Heading = ({ className, children, ...rest }) => (
  <h2 {...rest} className={classnames(className, 'text-lg font-medium mb-1')}>
    {children}
  </h2>
);
HomeMatchRateCard.Heading.displayName = 'HomeMatchRateCard.Heading';
